import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL || '';

const fmtDT = (d) => d ? formatDateTimeLocal(d) : '—';

export const AdjustmentHistoryDrawer = ({ employee, leaveTypeId, leaveYear, onClose }) => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchHistory = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/leave-registry/adjustments`, {
        params: { user_id: employee.user_id, leave_type_id: leaveTypeId, year: leaveYear, page: p, page_size: 10 }
      });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [employee.user_id, leaveTypeId, leaveYear]);

  useEffect(() => { fetchHistory(page); }, [page, fetchHistory]);

  return (
    <div className="fixed inset-0 z-[100] flex justify-end" data-testid="adjustment-history-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900">Adjustment History</h3>
            <p className="text-xs text-slate-500">{employee.employee_name} · {employee.leave_type_name} · {leaveYear}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="history-drawer-close"><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-4 space-y-3">
          {loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>}
          {!loading && data?.adjustments?.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-8">No adjustments recorded for this leave type and year.</p>
          )}
          {!loading && data?.adjustments?.map(adj => (
            <div key={adj.id} className="border border-slate-200 rounded-lg p-3 space-y-1" data-testid={`history-entry-${adj.id}`}>
              <div className="flex items-center justify-between">
                <Badge className={`border-0 ${adj.adjustment >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                  {adj.adjustment >= 0 ? '+' : ''}{adj.adjustment} day{Math.abs(adj.adjustment) !== 1 ? 's' : ''}
                </Badge>
                <span className="text-xs text-slate-400">{fmtDT(adj.created_at)}</span>
              </div>
              <p className="text-xs text-slate-600">
                Balance: <strong>{adj.previous_balance}</strong> → <strong>{adj.new_balance}</strong>
              </p>
              <p className="text-xs text-slate-600">Reason: {adj.reason}</p>
              {adj.notes && <p className="text-xs text-slate-500">Notes: {adj.notes}</p>}
              {adj.negative_balance_override && (
                <Badge variant="outline" className="text-[10px] text-rose-600 border-rose-200">Negative-balance override</Badge>
              )}
              {adj.bulk_operation_id && (
                <p className="text-[11px] text-slate-400">Bulk op: {adj.bulk_operation_id}</p>
              )}
              <p className="text-[11px] text-slate-400">By {adj.adjusted_by_name} · Ref {adj.id?.slice(0, 8)}</p>
            </div>
          ))}
          {data && data.total_pages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="history-prev">
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <span className="text-xs text-slate-500">Page {data.page} of {data.total_pages} ({data.total_items} entries)</span>
              <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)} data-testid="history-next">
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdjustmentHistoryDrawer;
